import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/app/xform-compat';
import { FilterFormData, DEFAULT_FILTER_FORM_DATA } from '../models/filter-form-data.interface';

/**
 * Handles localStorage persistence for the shared filter pane.
 * Each page passes its own storageKey so filter state is independent per page.
 */
@Injectable({ providedIn: 'root' })
export class FilterPanePersistenceService {
  constructor(private readonly localStorageService: LocalStorageService) {}

  /**
   * Saves filter form data to localStorage under the given key.
   */
  saveFilters(storageKey: string, formData: FilterFormData): void {
    this.localStorageService.set(storageKey, formData);
  }

  /**
   * Loads persisted filter form data from localStorage.
   * Returns null if no data is stored for the given key.
   * Applies defaults for any missing fields to handle schema changes gracefully.
   */
  loadFilters(storageKey: string): FilterFormData | null {
    const saved = this.localStorageService.get(storageKey);
    if (!saved) {
      return null;
    }

    return {
      mkCapLarge: saved.mkCapLarge ?? DEFAULT_FILTER_FORM_DATA.mkCapLarge,
      mkCapMedium: saved.mkCapMedium ?? DEFAULT_FILTER_FORM_DATA.mkCapMedium,
      mkCapSmall: saved.mkCapSmall ?? DEFAULT_FILTER_FORM_DATA.mkCapSmall,
      mkCapMicro: saved.mkCapMicro ?? DEFAULT_FILTER_FORM_DATA.mkCapMicro,
      mkCapLow: saved.mkCapLow ?? DEFAULT_FILTER_FORM_DATA.mkCapLow,
      mkCapHigh: saved.mkCapHigh ?? DEFAULT_FILTER_FORM_DATA.mkCapHigh,
      positionType: saved.positionType ?? DEFAULT_FILTER_FORM_DATA.positionType,
      following: saved.following ?? DEFAULT_FILTER_FORM_DATA.following,
      domainIds: (saved.domainIds ?? DEFAULT_FILTER_FORM_DATA.domainIds).map(Number),
      portfolioIds: (saved.portfolioIds ?? DEFAULT_FILTER_FORM_DATA.portfolioIds).map(Number),
    };
  }

  /**
   * Removes persisted filter data for the given key.
   */
  clearFilters(storageKey: string): void {
    this.localStorageService.delete(storageKey);
  }
}
